// Signature pad (design system: dashed pad, finger drawing, Clear, Done). Returns a small PNG
// data URL. Shared by every module that captures a signature. The canvas is sized from its
// box whenever that box changes (a pad can mount while the page is hidden or mid-layout),
// and Done never throws on an unsized canvas.
import { useEffect, useRef, useState } from 'react';

export function SignaturePad({ onDone, onCancel, title, statement }: { onDone: (png: string) => void; onCancel: () => void; title: string; statement: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  function setup(c: HTMLCanvasElement) {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = c.clientWidth, h = c.clientHeight;
    if (!w || !h) return false;
    c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
    const ctx = c.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--fg').trim() || '#1a2029';
    return true;
  }
  useEffect(() => {
    const c = ref.current!;
    setup(c);
    // Re-size (and so clear) when the box first gets a size or changes; a drawn signature is kept until then.
    // Set up once: re-running on the first stroke resized the canvas and wiped what was drawn.
    const ro = new ResizeObserver(() => { if (!hasInk.current && (c.width === 0 || Math.abs(c.clientWidth * Math.min(2, window.devicePixelRatio || 1) - c.width) > 2)) setup(c); });
    ro.observe(c);
    return () => ro.disconnect();
  }, []);

  function pt(e: React.PointerEvent<HTMLCanvasElement>) { const r = ref.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = ref.current!; if (!c.width) setup(c);
    drawing.current = true; try { c.setPointerCapture(e.pointerId); } catch { /* fine */ }
    const p = pt(e); const ctx = c.getContext('2d')!; ctx.beginPath(); ctx.moveTo(p.x, p.y);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) { if (!drawing.current) return; const p = pt(e); const ctx = ref.current!.getContext('2d')!; ctx.lineTo(p.x, p.y); ctx.stroke(); hasInk.current = true; setDrawn(true); }
  function up() { drawing.current = false; }
  function clear() { const c = ref.current!; const ctx = c.getContext('2d')!; ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height); ctx.restore(); hasInk.current = false; setDrawn(false); }
  function done() {
    const src = ref.current!;
    if (!src.width || !src.height) { setDrawn(false); return; }
    const out = document.createElement('canvas'); out.width = 300; out.height = 100;
    const ctx = out.getContext('2d')!; ctx.drawImage(src, 0, 0, 300, 100);
    // The pad draws in the theme's text color so it reads on screen; the saved signature is always
    // ink, or one signed at night prints white on the PDF.
    ctx.globalCompositeOperation = 'source-in'; ctx.fillStyle = '#1a2029'; ctx.fillRect(0, 0, 300, 100);
    onDone(out.toDataURL('image/png'));
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end md:items-center md:justify-center" style={{ background: 'var(--scrim, rgba(0,0,0,.5))' }}>
      <div className="bg-surface w-full md:max-w-[520px] rounded-t-[22px] md:rounded-[18px] px-3.5 pt-3 pb-7 flex flex-col gap-2.5" style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }}>
        <div className="w-10 h-1 rounded bg-line mx-auto md:hidden" />
        <h5 className="font-head font-bold text-[17px] m-0">{title}</h5>
        <p className="m-0 text-[13px] text-muted">{statement}</p>
        <div className="relative h-[150px] rounded-[12px] bg-bg border border-dashed border-line touch-none">
          <canvas ref={ref} className="absolute inset-0 w-full h-full" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
          <div className="absolute left-4 right-4 bottom-[34px] border-t border-line pointer-events-none" />
          {!drawn && <div className="absolute inset-x-0 bottom-2.5 text-center text-muted text-[12.5px] pointer-events-none">Sign with a finger</div>}
        </div>
        <div className="flex gap-2">
          <button className="btn flex-1" onClick={clear}>Clear</button>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary flex-[2]" disabled={!drawn} onClick={done}>Done signing</button>
        </div>
      </div>
    </div>
  );
}
